import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet, ScrollView, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { ClientType } from "@/types/db/clients";
import { useTheme } from "@/hooks/useTheme";
import Modal from "@/components/Modal";
import { ModalCard } from "@/components/Modals/ModalCard";
import { LocationsPicker } from "@/components/Pickers/LocationsPicker";
import { supabase } from "@/lib/supabase-client";
import usePermissions from "@/hooks/usePermissions";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { SkeletonRow } from "@/components/ui/Skeleton";
import { clientService } from "@/services/clientService";
import useToast from "@/hooks/useToast";

type ClientLocation = {
  location: {
    id: string;
    name: string;
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    description?: string;
    instructions?: string;
  };
};

export interface ClientLocationsScreenProps {
  externalPickerTrigger?: number;
}

export function ClientLocationsScreen({ externalPickerTrigger }: ClientLocationsScreenProps) {
  const { entityData, loading } = useEntityEdit<ClientType>();
  const { clients: clientPermissions } = usePermissions();
  const { colors } = useTheme();
  const toast = useToast();

  const [locationPickerVisible, setLocationPickerVisible] = useState(false);
  const [locations, setLocations] = useState<ClientLocation[]>([]);
  const [saving, setSaving] = useState(false);

  // Open picker when external trigger changes
  useEffect(() => {
    if (externalPickerTrigger && externalPickerTrigger > 0) {
      setLocationPickerVisible(true);
    }
  }, [externalPickerTrigger]);

  // Sync local state with entity data
  useEffect(() => {
    if (entityData?.client_locations) {
      setLocations(entityData.client_locations);
    }
  }, [entityData?.client_locations]);

  // Handle location removal
  const handleRemoveLocation = useCallback(
    async (index: number) => {
      if (!entityData?.id) return;

      const updated = locations.filter((_, i) => i !== index);
      setSaving(true);
      try {
        await clientService.syncClientLocations(entityData.id, updated);
        setLocations(updated);
        toast.showToast({ title: "Saved", message: "Location removed", toastType: "success" });
      } catch (error: any) {
        toast.showToast({ title: "Error", message: error.message, toastType: "error" });
      } finally {
        setSaving(false);
      }
    },
    [entityData?.id, locations, toast],
  );

  // Handle picking locations
  const handlePickLocations = useCallback(
    async (locationIds: string[]) => {
      if (!entityData?.id) {
        setLocationPickerVisible(false);
        return;
      }

      // Fetch full location details for each ID
      const { data: fetchedLocations, error } = await supabase
        .schema("lists")
        .from("locations")
        .select("*")
        .in("id", locationIds);

      if (error) {
        console.error("Failed to fetch location details:", error);
        toast.showToast({ title: "Error", message: "Failed to fetch locations", toastType: "error" });
        setLocationPickerVisible(false);
        return;
      }

      // Map to client_locations structure
      const updated: ClientLocation[] = (fetchedLocations || []).map((location) => ({
        location,
      }));

      setSaving(true);
      try {
        await clientService.syncClientLocations(entityData.id, updated);
        setLocations(updated);
        toast.showToast({ title: "Saved", message: "Locations updated", toastType: "success" });
      } catch (error: any) {
        toast.showToast({ title: "Error", message: error.message, toastType: "error" });
      } finally {
        setSaving(false);
        setLocationPickerVisible(false);
      }
    },
    [entityData?.id, toast],
  );

  // If no read permission, show access denied
  if (!clientPermissions.read) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <MaterialCommunityIcons name="lock" size={48} color={colors.textSecondary} />
        <ThemedText type="defaultSemiBold" style={{ marginTop: 16 }}>
          Access Denied
        </ThemedText>
        <ThemedText style={{ opacity: 0.7, marginTop: 8 }}>You don&apos;t have permission to view locations</ThemedText>
      </ScrollView>
    );
  }

  if (loading || !entityData) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <SkeletonRow />
          <SkeletonRow />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container} pointerEvents={saving ? "none" : "auto"}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {locations.length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="map-marker-outline" size={48} color={colors.textSecondary} />
            <ThemedText style={{ opacity: 0.7, marginTop: 12 }}>No locations assigned</ThemedText>
            {clientPermissions.update && (
              <ThemedText style={{ opacity: 0.5, marginTop: 4, fontSize: 12 }}>
                Use the + button to add locations
              </ThemedText>
            )}
          </View>
        ) : (
          <View style={styles.locationsList}>
            {locations.map((clientLocation, index) => {
              const location = clientLocation.location;
              const fullAddress = [location.street, location.city, location.state, location.zip]
                .filter(Boolean)
                .join(", ");

              return (
                <View
                  key={location.id}
                  style={[
                    styles.locationCard,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.border,
                    },
                  ]}
                >
                  <View style={styles.locationHeader}>
                    <View style={styles.locationInfo}>
                      <ThemedText type="defaultSemiBold" style={{ fontSize: 16 }}>
                        {location.name}
                      </ThemedText>
                      {fullAddress && (
                        <ThemedText style={{ opacity: 0.6, marginTop: 4, fontSize: 13 }}>{fullAddress}</ThemedText>
                      )}
                    </View>
                    {clientPermissions.update && (
                      <Pressable style={styles.removeButton} onPress={() => handleRemoveLocation(index)}>
                        <MaterialCommunityIcons name="close" size={20} color={colors.red?.text || colors.text} />
                      </Pressable>
                    )}
                  </View>

                  {location.description && (
                    <ThemedText style={{ opacity: 0.7, fontSize: 13, marginTop: 8 }}>{location.description}</ThemedText>
                  )}

                  {location.instructions && (
                    <View style={[styles.instructionsContainer, { backgroundColor: colors.amber?.background }]}>
                      <MaterialCommunityIcons name="information-outline" size={14} color={colors.amber?.text} />
                      <ThemedText style={[styles.instructionsText, { color: colors.amber?.text }]}>
                        Has special instructions
                      </ThemedText>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </ScrollView>

      {/* Location Picker Modal */}
      {locationPickerVisible && (
        <Modal onUnmount={() => setLocationPickerVisible(false)}>
          <ModalCard>
            <LocationsPicker
              selectedIds={locations.map((cl) => cl.location.id)}
              onPick={handlePickLocations}
              onCancel={() => setLocationPickerVisible(false)}
            />
          </ModalCard>
        </Modal>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    gap: 16,
  },
  loadingContainer: {
    padding: 16,
    gap: 12,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 48,
  },
  locationsList: {
    gap: 12,
  },
  locationCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
  },
  locationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  locationInfo: {
    flex: 1,
  },
  removeButton: {
    padding: 4,
    marginLeft: 8,
  },
  instructionsContainer: {
    flexDirection: "row",
    gap: 6,
    padding: 8,
    borderRadius: 6,
    marginTop: 8,
    alignSelf: "flex-start",
  },
  instructionsText: {
    fontSize: 11,
    fontWeight: "600",
  },
});
